import { DynamicIcon, type IconName } from "lucide-react/dynamic";
import React from "react";
import {
  breakpointClasses,
  cn,
  resolveElement,
  splitBreakpointProps,
} from "@/lib/utils";
import LinkButton from "../Link";
import Button from "../Root";
import type { IconButtonElement, IconButtonProps } from "./types";

function ButtonIcon<E extends React.ElementType = IconButtonElement>({
  as,
  asChild = false,
  label,
  orientation = "left",
  icon,
  href,
  className,
  children,
  ref,
  ...props
}: IconButtonProps<E>) {
  const Component = resolveElement(asChild, as, Button);
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  if (process.env.NODE_ENV !== "production" && as && asChild) {
    console.warn(
      `[Button] 'asChild' renders the child element; 'as' is ignored.`,
    );
  }

  const renderIcon = () => {
    if (!icon) {
      return null;
    }

    if (typeof icon === "string") {
      return (
        <DynamicIcon
          name={icon as IconName}
          aria-hidden={true}
          focusable={false}
        />
      );
    }

    if (React.isValidElement(icon)) {
      return icon;
    }

    if (typeof icon === "function") {
      const IconComp = icon;

      return <IconComp />;
    }

    return null;
  };

  if (href) {
    return (
      <LinkButton
        href={href}
        className={cn("relative p-2 gap-2", className, classes)}
        {...native}
      >
        {orientation === "left" && renderIcon()}
        {label}
        {orientation === "right" && renderIcon()}

        {children}
      </LinkButton>
    );
  }

  return (
    <Component
      ref={ref}
      className={cn("relative p-2 gap-2", className, classes)}
      {...native}
    >
      {orientation === "left" && renderIcon()}
      {label}
      {orientation === "right" && renderIcon()}

      {children}
    </Component>
  );
}

export default ButtonIcon;
